import React from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Table,
  Badge,
} from "react-bootstrap";
import { MecordColors } from "Constants/Color";
import LoadingSpinner from "components/LoadingSpinner";
import CancelButton from "../Buttons/CancelButton";

interface MecordPODetails {
  po_number: string;
  po_date: string | Date;
}

interface CustomerSalesOrderDetails {
  invoice_number: string;
  invoice_date: string | Date;
}

interface SalesHistory {
  mecord_po: MecordPODetails | null;
  customer_sales_order: CustomerSalesOrderDetails | null;
}

interface ServiceHistoryItem {
  service_number: string;
  service_date: string | Date;
}

interface ProductHistoryData {
  serial_no: string;
  sales_history: SalesHistory;
  service_history: ServiceHistoryItem[];
}

interface ProductHistoryModalProps {
  show: boolean;
  onHide: () => void;
  serialNo: string;
  productHistory: ProductHistoryData | null;
  loading: boolean;
}

const ProductHistoryModal: React.FC<ProductHistoryModalProps> = ({
  show,
  onHide,
  serialNo,
  productHistory,
  loading,
}) => {
  return (
    <>
      <style>
        {`
          .modal-header .btn-close {
            filter: brightness(0) invert(1);
            margin-bottom: 5px;
          }
        `}
      </style>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: MecordColors?.ThemeBGColor || "#007bff",
            color: "#fff",
            borderBottom: "none",
            paddingBottom: "10px"
          }}
        >
        <Modal.Title style={{ fontWeight: 600, fontSize: "20px", color: "#fff", marginBottom: "5px" }}>
          Product History - Serial No: {serialNo}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <LoadingSpinner label="Loading product history..." />
        ) : productHistory ? (
          <div>
            {/* Sales History Section */}
            <Card className="mb-4" style={{ border: "1px solid #e0e0e0" }}>
              <Card.Header
                style={{
                  backgroundColor: "#f8f9fa",
                  fontWeight: 600,
                  fontSize: "16px",
                  borderBottom: "2px solid #007bff",
                  paddingBottom: "7px",
                }}
              >
                Sales History
              </Card.Header>
              <Card.Body>
                <Row>
                  {/* Mecord PO Details */}
                  <Col md={6} className="mb-3">
                    <div
                      style={{
                        padding: "15px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      <h6
                        style={{
                          color: "#495057",
                          fontWeight: 600,
                          marginBottom: "10px",
                        }}
                      >
                        Mecord PO Details
                      </h6>
                      {productHistory.sales_history?.mecord_po ? (
                        <div>
                          <p style={{ margin: "5px 0" }}>
                            <strong>PO Number:</strong>{" "}
                            {productHistory.sales_history.mecord_po.po_number}
                          </p>
                          <p style={{ margin: "5px 0" }}>
                            <strong>PO Date:</strong>{" "}
                            {new Date(
                              productHistory.sales_history.mecord_po.po_date
                            ).toLocaleDateString("en-IN", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      ) : (
                        <p style={{ color: "#6c757d", fontStyle: "italic" }}>
                          No PO details available
                        </p>
                      )}
                    </div>
                  </Col>

                  {/* Customer Sales Order Details */}
                  <Col md={6} className="mb-3">
                    <div
                      style={{
                        padding: "15px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      <h6
                        style={{
                          color: "#495057",
                          fontWeight: 600,
                          marginBottom: "10px",
                        }}
                      >
                        Customer Sales Order Details
                      </h6>
                      {productHistory.sales_history?.customer_sales_order ? (
                        <div>
                          <p style={{ margin: "5px 0" }}>
                            <strong>Invoice Number:</strong>{" "}
                            {
                              productHistory.sales_history.customer_sales_order
                                .invoice_number
                            }
                          </p>
                          <p style={{ margin: "5px 0" }}>
                            <strong>Invoice Date:</strong>{" "}
                            {new Date(
                              productHistory.sales_history.customer_sales_order
                                .invoice_date
                            ).toLocaleDateString("en-IN", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      ) : (
                        <p style={{ color: "#6c757d", fontStyle: "italic" }}>
                          No sales order details available
                        </p>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Service History Section */}
            <Card style={{ border: "1px solid #e0e0e0" }}>
              <Card.Header
                style={{
                  backgroundColor: "#f8f9fa",
                  fontWeight: 600,
                  fontSize: "16px",
                  borderBottom: "2px solid #28a745",
                  paddingBottom: "7px",
                }}
              >
                Service History
                {productHistory.service_history?.length > 0 && (
                  <Badge
                    bg="success"
                    style={{ marginLeft: "10px", fontSize: "12px" }}
                  >
                    {productHistory.service_history.length} Service(s)
                  </Badge>
                )}
              </Card.Header>
              <Card.Body>
                {productHistory.service_history?.length > 0 ? (
                  <Table striped bordered hover responsive>
                    <thead style={{ backgroundColor: "#e9ecef" }}>
                      <tr>
                        <th style={{ width: "50px" }}>#</th>
                        <th>Service Number</th>
                        <th>Service Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productHistory.service_history.map(
                        (service: ServiceHistoryItem, index: number) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <Badge bg="info" style={{ fontSize: "13px" }}>
                                {service.service_number}
                              </Badge>
                            </td>
                            <td>
                              {new Date(service.service_date).toLocaleDateString(
                                "en-IN",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </Table>
                ) : (
                  <p
                    style={{
                      color: "#6c757d",
                      fontStyle: "italic",
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    No service history available
                  </p>
                )}
              </Card.Body>
            </Card>
          </div>
        ) : (
          <div className="text-center py-4">
            <p style={{ color: "#6c757d" }}>
              No product history found for this serial number.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "center" }}>
        <CancelButton onClick={onHide} showConfirmation={false} />
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default ProductHistoryModal;
